import React, { useEffect, useState, useRef } from 'react';
import './MapDrawer.css';

interface MapDrawerProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  hasOverlay?: boolean;
  title: string;
  subtitle?: string;
  fullscreen?: boolean; // ✅ NOUVEAU : Mode plein écran
}

const MapDrawer: React.FC<MapDrawerProps> = ({
  children,
  isOpen,
  onClose,
  className = '',
  hasOverlay = true,
  title,
  subtitle,
  fullscreen = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isFullyOpen, setIsFullyOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Ref pour le drag tracking (pas de state pour éviter les closures stales)
  const dragRef = useRef({
    isDragging: false,
    startY: 0,
    currentY: 0,
    velocity: 0,
    lastTime: 0
  });

  // Ref stable pour onClose (évite de recréer les listeners à chaque render)
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Handle opening/closing
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsClosing(false);
      setIsReady(false);
      setIsFullyOpen(false);

      const mountingDelay = setTimeout(() => {
        requestAnimationFrame(() => {
          setIsReady(true);
          requestAnimationFrame(() => {
            setIsFullyOpen(true);
          });
        });
      }, 50);

      return () => clearTimeout(mountingDelay);
    } else {
      setIsClosing(true);
      setIsFullyOpen(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsReady(false);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // 📱 Touch handlers natifs (non-passifs pour permettre preventDefault)
  useEffect(() => {
    const el = drawerRef.current;
    if (!el || !isVisible) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      const target = e.target as HTMLElement;
      const listElement = target.closest('.preview-card-map__list');

      if (listElement && listElement.scrollTop > 5) return;

      const drag = dragRef.current;
      drag.isDragging = true;
      drag.startY = touch.clientY;
      drag.currentY = touch.clientY;
      drag.velocity = 0;
      drag.lastTime = Date.now();

      el.classList.add('map-drawer--dragging');
    };

    const handleTouchMove = (e: TouchEvent) => {
      const drag = dragRef.current;
      if (!drag.isDragging) return;

      const touch = e.touches[0];
      const target = e.target as HTMLElement;
      const listElement = target.closest('.preview-card-map__list');

      if (listElement && listElement.scrollTop > 5) {
        el.style.removeProperty('--drag-offset');
        el.classList.remove('map-drawer--dragging');
        drag.isDragging = false;
        drag.velocity = 0;
        return;
      }

      const currentTime = Date.now();
      const deltaTime = Math.max(1, currentTime - drag.lastTime);
      const deltaY = touch.clientY - drag.currentY;

      drag.velocity = deltaY / deltaTime;
      const totalDeltaY = touch.clientY - drag.startY;

      if (totalDeltaY > 0) {
        e.preventDefault(); // Fonctionne car listener natif non-passif
      }

      el.style.setProperty('--drag-offset', `${Math.max(0, totalDeltaY)}px`);

      drag.currentY = touch.clientY;
      drag.lastTime = currentTime;
    };

    const handleTouchEnd = () => {
      const drag = dragRef.current;
      if (!drag.isDragging) return;

      const totalDeltaY = drag.currentY - drag.startY;
      const shouldClose = totalDeltaY > 100 || drag.velocity > 0.5;

      el.style.removeProperty('--drag-offset');
      el.classList.remove('map-drawer--dragging');

      drag.isDragging = false;
      drag.velocity = 0;

      if (shouldClose) {
        onCloseRef.current();
      }
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isVisible]);

  // Gestion de l'échap
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Empêcher le scroll du body quand le drawer est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isVisible) return null;

  return (
    <>
      {hasOverlay && (
        <div
          className={`map-drawer-overlay ${isOpen ? 'map-drawer-overlay--visible' : ''}`}
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div className={`map-drawer-container ${fullscreen ? 'map-drawer-container--fullscreen' : ''} ${className}`}>
        <div
          ref={drawerRef}
          className={`map-drawer ${isReady ? 'map-drawer--ready' : ''} ${isFullyOpen ? 'map-drawer--open' : ''} ${isClosing ? 'map-drawer--closing' : ''}`}
          role="dialog"
          aria-modal="true"
        >
          <header className="map-drawer__header">
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </header>
          {children}
        </div>
      </div>
    </>
  );
};

export default MapDrawer;
